import * as React from 'react'
import { ReactElement, useCallback, useMemo, useRef } from 'react'
import {
    Button,
    makeStyles,
    mergeClasses,
    Popover,
    PopoverSurface,
    PopoverTrigger,
    PositioningImperativeRef,
    Subtitle2,
    Text,
    Tooltip,
    tokens,
} from '@fluentui/react-components'
import { Calendar } from '@fluentui/react-calendar-compat'
import { useToggle } from 'usehooks-ts'
import { useTranslation } from 'react-i18next'
import { AnyDate, DateFormats, formatDate, parseDate } from '../../helpers/dateHelper'
import { defaultCalendarProps, useCalendarStrings } from '../DatePicker/LocalDatePicker'

const useStyles = makeStyles({
    popoverSurface: {
        display: 'grid',
        gridTemplateRows: '40px auto 32px',
        rowGap: tokens.spacingVerticalS,
        backgroundColor: tokens.colorNeutralBackground3,
    },
    popoverContent: {
        backgroundColor: tokens.colorNeutralBackground1,
    },
    // A real button, so an overview row leaves the click alone: BaseOverviewPage only ignores clicks that come from an
    // input, button or link. It has to read as plain text though, hence the stripped down paddings and weight.
    date: {
        minWidth: 'auto',
        height: 'auto',
        padding: tokens.spacingHorizontalXXS,
        border: 'none',
        fontWeight: tokens.fontWeightRegular,
        whiteSpace: 'nowrap',
        backgroundColor: 'transparent',

        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground2,
            borderRadius: tokens.borderRadiusMedium,
        },
    },
})

export type EditableDateProps = {
    date: AnyDate
    /** What the tooltip spells out, typically the same date in full notation. Left out when there is nothing to add. */
    tooltip?: string
    /** The notation of the date itself; the short one keeps a narrow overview readable. */
    format?: DateFormats
    className?: string
    setDate: (date: Date) => void
}

/**
 * One date in an overview that the user can change on the spot: clicking it opens a calendar (HAM-4286). Split off
 * from the task period so every date of a task can be edited on its own, instead of only the due date.
 */
export default function EditableDate({ setDate, ...props }: EditableDateProps): ReactElement {
    const styles = useStyles()
    const { t: translate, i18n } = useTranslation()
    const calendarStrings = useCalendarStrings()

    const dateValue = useMemo<Date>(() => new Date(parseDate(props.date || new Date()).toBSON()), [props.date])
    const dateString = useMemo(
        () =>
            props.format
                ? parseDate(props.date, { locale: i18n.language })?.toFormat(props.format)
                : formatDate(props.date, { locale: i18n.language }),
        [i18n.language, props.date, props.format],
    )

    const [isOpen, , setOpen] = useToggle(false)
    const positioningRef = useRef<PositioningImperativeRef>(null)

    const triggerRef = useCallback(
        (el: HTMLElement) => {
            positioningRef.current?.setTarget(el)
        },
        [positioningRef],
    )

    return (
        <Popover open={isOpen} onOpenChange={(_event, data) => setOpen(data.open)} positioning={{ positioningRef }}>
            <Tooltip relationship={'label'} content={props.tooltip || dateString} withArrow>
                <PopoverTrigger disableButtonEnhancement>
                    <Button ref={triggerRef} appearance={'transparent'} className={mergeClasses(styles.date, props.className)}>
                        <Text size={200}>{dateString}</Text>
                    </Button>
                </PopoverTrigger>
            </Tooltip>

            <PopoverSurface tabIndex={-1} className={styles.popoverSurface}>
                <Subtitle2>{translate('base.datepicker.placeholder')}</Subtitle2>
                <div className={styles.popoverContent}>
                    <Calendar
                        {...defaultCalendarProps}
                        strings={calendarStrings}
                        showMonthPickerAsOverlay
                        highlightSelectedMonth
                        value={dateValue}
                        onSelectDate={date => {
                            setDate(date)
                            setOpen(false)
                        }}
                        onDismiss={() => setOpen(false)}
                    />
                </div>
                <Button appearance={'secondary'} onClick={() => setOpen(false)}>
                    {translate('base.alert.cancel')}
                </Button>
            </PopoverSurface>
        </Popover>
    )
}
